/**
 * Base error for all custom errors in stickerizer
 */
export class BaseError extends Error {
  public code: string;
  public details?: unknown;
  constructor(message: string, code: string, details?: unknown) {
    super(message);
    this.name = this.constructor.name;
    this.code = code;
    this.details = details;
    Error.captureStackTrace?.(this, this.constructor);
  }
}

/**
 * Root error for stickerizer domain
 */
export class StickerizerError extends BaseError {
  constructor(message: string, code = 'STICKERIZER_ERROR', details?: unknown) {
    super(message, code, details);
  }
}

export class InvalidConstructorCallError extends StickerizerError {
  constructor(details?: unknown) {
    super(
      'Stickerizer must be instantiated using Stickerizer.create(). Direct constructor calls are not allowed.',
      'INVALID_CONSTRUCTOR_CALL',
      details,
    );
  }
}

export class InvalidOriginalImageSizeLimitError extends StickerizerError {
  constructor(details?: unknown) {
    super(
      'Original image size is too large. Please resize the image.',
      'INVALID_ORIGINAL_IMAGE_SIZE_LIMIT',
      details,
    );
  }
}

export class InvalidApiResponseError extends StickerizerError {
  constructor(api: string, details?: unknown) {
    super(`Invalid response from ${api} API`, 'INVALID_API_RESPONSE', details);
  }
}

export class NotFoundInCacheError extends StickerizerError {
  constructor(context?: string, details?: unknown) {
    super(`No cached info for given imageUrl${context ? ': ' + context : ''}`, 'NOT_FOUND_IN_CACHE', details);
  }
}

export class NoCutlineSvgWithImageError extends StickerizerError {
  constructor(details?: unknown) {
    super(
      'No cutline svg with image for given imageUrl. Please execute process method first.',
      'NO_CUTLINE_SVG_WITH_IMAGE',
      details,
    );
  }
}

export class InstanceNotInitializedError extends StickerizerError {
  constructor(details?: unknown) {
    super(
      'Stickerizer instance is not properly initialized. Please use Stickerizer.create() method first.',
      'INSTANCE_NOT_INITIALIZED',
      details,
    );
  }
}

export class InvalidInputError extends StickerizerError {
  constructor(message: string, details?: unknown) {
    super(message, 'INVALID_INPUT', details);
  }
}

/**
 * API request failure (네트워크, 응답코드 등 포함)
 */
export class ApiRequestError extends StickerizerError {
  constructor(message: string, details?: unknown) {
    super(message, 'API_REQUEST_ERROR', details);
  }
}

/**
 * API timeout (fetch timeout)
 */
export class ApiTimeoutError extends StickerizerError {
  constructor(message = 'API request timed out', details?: unknown) {
    super(message, 'API_TIMEOUT_ERROR', details);
  }
}

/**
 * API 네트워크 오류 (fetch 자체 실패 등)
 */
export class ApiNetworkError extends StickerizerError {
  constructor(message = 'API network error', details?: unknown) {
    super(message, 'API_NETWORK_ERROR', details);
  }
}

/**
 * API request aborted (이전 요청 취소)
 */
export class ApiAbortError extends StickerizerError {
  constructor(message = 'API request was aborted', details?: unknown) {
    super(message, 'API_ABORT_ERROR', details);
  }
}
